import './qds-dialog.js';
import type QdsDialog from './qds-dialog.js';

export interface ConfirmDialogOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** Returning false or rejecting keeps the confirmation open for retry. */
  onConfirm?: (signal: AbortSignal) => unknown | Promise<unknown>;
  /** Append within a modal dialog when confirming a nested workflow. */
  container?: HTMLElement;
  returnFocus?: HTMLElement;
}

/** Text-only confirmation composition. No HTML interpolation or browser dependencies. */
export function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  // The registration may already come from a different standalone bundle.
  const dialog = document.createElement('qds-dialog') as QdsDialog;
  dialog.setAttribute('data-qds-confirm', '');
  dialog.setAttribute('label', options.title);
  dialog.returnFocus = options.returnFocus || null;
  const message = document.createElement('p'); message.textContent = options.message;
  const actions = document.createElement('div'); actions.slot = 'footer';
  actions.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;flex-wrap:wrap;';
  const cancel = document.createElement('button'), confirm = document.createElement('button');
  cancel.type = confirm.type = 'button';
  cancel.textContent = options.cancelLabel || 'Cancel'; confirm.textContent = options.confirmLabel || 'Confirm';
  for (const button of [cancel, confirm]) {
    button.style.cssText = 'min-height:44px;padding:8px 16px;border:1px solid var(--theme-border-color,#e0e0e0);border-radius:4px;background:var(--theme-background-primary,#fff);color:var(--theme-text-primary,#333);font:inherit;cursor:pointer;';
  }
  if (options.destructive) {
    confirm.style.background = 'var(--theme-error, #8b1f00)';
    confirm.style.color = 'var(--theme-text-on-error, #fff)';
  }
  const focusStyle = document.createElement('style');
  focusStyle.textContent = 'qds-dialog[data-qds-confirm] > [slot="footer"] > button:focus-visible { outline: 2px solid var(--theme-accent-primary, #4285f4); outline-offset: 2px; }';
  dialog.initialFocus = cancel;
  actions.append(cancel, confirm); dialog.append(message, actions, focusStyle);
  return new Promise(resolve => {
    dialog.addEventListener('close', event => {
      resolve((event as CustomEvent).detail.returnValue === 'confirm'); dialog.remove();
    }, { once: true });
    cancel.addEventListener('click', () => dialog.requestClose('api', 'cancel'));
    confirm.addEventListener('click', async () => {
      cancel.disabled = confirm.disabled = true;
      await dialog.runAction(signal => options.onConfirm?.(signal), 'confirm');
      cancel.disabled = confirm.disabled = false;
    });
    (options.container || document.body).appendChild(dialog);
    dialog.show();
  });
}
